"use client";

import { RefreshCw } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { AccountSwitchInfo } from "@/lib/api-client";

/**
 * Screen-covering, non-dismissable notice shown once another tab of this
 * browser signed in as a different account (or signed out). The tab's data
 * calls are already frozen; the only way forward is a reload, which lands
 * under the new session (or on /login).
 */
export function AccountSwitchedDialog({ info }: { info: AccountSwitchInfo | null }) {
  if (!info) {
    return null;
  }

  const signedOut = info.subject === null;
  const title = signedOut ? "Signed out in another tab" : "Signed in as another account";
  const description = signedOut
    ? "This browser no longer has a Scirix session. Reload to sign in again."
    : info.email
      ? `Another tab signed in as ${info.email}. A browser can hold one Scirix session at a time, so this page has been paused to keep the two accounts apart. Reload to continue as ${info.email}.`
      : "Another tab signed in as a different account. A browser can hold one Scirix session at a time, so this page has been paused to keep the two accounts apart. Reload to continue.";

  return (
    <AlertDialog open>
      <AlertDialogContent size="sm" onEscapeKeyDown={(event) => event.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6">
          <AlertDialogAction onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" aria-hidden />
            Reload
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
